<template>
    <el-table
        ref="student-table"
        :data="items"
        :default-sort="{ prop: sort_by }"
        class="student-table"
        style="width: 100%"
        empty-text="No Students"
        @row-click="navigateToStudentDetail"
        @sort-change="handleSortChange">
        <el-table-column 
            v-if="showColumn('bulk') && !isMobile()" 
            :render-header="renderHeader"
            width="55">
            <template slot-scope="scope">
                <app-checkbox
                    :value="isSelected(scope.row)"
                    :disabled="scope.row.deleted_at != null"
                    size="large"
                    accessibility-label="Select Bulk Action"
                    title="Select Bulk Action"
                    @change="toggleSelect($event, scope.row)"
                    @click.native.stop/>
            </template>
        </el-table-column>
        <el-table-column v-if="showColumn('attendance')" label="Status" width="85px">
            <template slot-scope="scope">
                <status-block
                    :attendance="scope.row.attendance[0]"
                    @selected="viewAttendance(scope.row.uuid)"/>
            </template>
        </el-table-column>
        <el-table-column
            v-if="showColumn('apa')"
            :sortable="sortable"
            prop="semester_apa"
            width="90px"
            label="APA">
            <template slot-scope="scope">{{ apaFormat(scope.row.semester_apa) }}</template>
        </el-table-column>
        <el-table-column
            v-if="showColumn('first_name')"
            :sortable="sortable"
            prop="first_name"
            label="First Name"
            min-width="80px">
            <template slot-scope="scope">
                <router-link
                    :aria-label="'Student: '+ nameFormat(scope.row)"
                    :to="{ name: 'students.detail', params: { uuid: scope.row.uuid } }"
                    event
                    @click.prevent="navigateToStudentDetail(scope.row)">{{ scope.row.first_name }}</router-link>
            </template>
        </el-table-column>
        <el-table-column
            v-if="showColumn('last_name')"
            :sortable="sortable"
            prop="last_name"
            label="Last Name"
            min-width="80px"/>
        <el-table-column
            v-if="showColumn('email')"
            :sortable="sortable"
            prop="email"
            label="Email"
            min-width="100px"/>
        <el-table-column 
            v-if="showColumn('phone')" 
            :sortable="sortable" 
            prop="phone" 
            label="Phone">
            <template slot-scope="scope">{{ phoneFormat(scope.row.phone) }}</template>
        </el-table-column>
        <el-table-column 
            v-if="showColumn('last_seen')" 
            :sortable="sortable" 
            prop="last_seen" 
            label="Last Seen">
            <template slot-scope="scope">
                <template v-if="scope.row.last_seen">
                    {{ [scope.row.last_seen + '-00:00'] | moment("from") }}
                </template>
                <template v-else>–</template>
            </template>
        </el-table-column>
        <el-table-column
            v-if="showColumn('state')"
            prop="deleted_at"
            label="State"
            width="100px">
            <template slot-scope="scope">
                <app-tag
                    v-if="scope.row.deleted_at"
                    slot="tag"
                    type="danger"
                    size="small">Archived</app-tag>
            </template>
        </el-table-column>
        <slot name="additional"/>
        <slot name="actions"/>
    </el-table>
</template>

<script>
import { mapState } from 'vuex'
import { phoneFormat, apaFormat, nameFormat, isMobile, contains } from '@/utilities'
import StatusBlock from '../../ui/StatusBlock'
export default {
    name: 'student-table',
    components: { StatusBlock },
    props: {
        items: {
            type: Array
        },
        bulkAction: String,
        sort_by: String,
        columns: {
            type: Array,
            default() {
                return [
                    'apa',
                    'first_name',
                    'last_name',
                    'email',
                    'last_seen',
                    'attendance'
                ]
            }
        },
        attendable: {
            type: Object,
            default() {
                return { uuid: null }
            }
        },
        attendableType: {
            type: String,
            default: 'course'
        },
        allowSort: {
            type: Boolean,
            default: true
        },
        handleClick: {
            default() {
                return student => {
                    this.$router.push({
                        name: 'students.detail',
                        params: { uuid: student.uuid }
                    })
                }
            }
        },
    },
    computed: {
        ...mapState({
            bulkStudents: ({ studentList }) => studentList.bulkStudents
        }),
        sortable() {
            return this.allowSort ? 'custom' : false
        }
    },
    methods: {
        navigateToStudentDetail(student) {
            this.handleClick(student)
        },
        handleSortChange(properties) {
            this.$emit('sort-change', properties)
        },
        renderHeader() {
            return <app-checkbox value={this.areAllSelected()}
                size="large"
                accessibility-label="Select All Bulk Action"
                title="Select All Bulk Action"
                onChange={this.toggleAll}
                onClickNative={evt => {
                    evt.preventDefault();
                }} />
        },
        showColumn(column) {
            return this.columns.indexOf(column) >= 0
        },
        viewAttendance(studentUuid) {
            this.$router.push({
                name: 'students.detail.' + this.attendableType + 's.attendance',
                params: {
                    studentUuid: studentUuid,
                    type: this.attendableType,
                    attendableUuid: this.attendable.uuid
                }
            })
        },
        isSelected(item) {
            return this.bulkStudents.indexOf(item.id) >= 0
        },
        toggleSelect(event, item) {
            const index = this.bulkStudents.indexOf(item.id)
            if (index >= 0) {
                this.$store.commit('BULK_STUDENT_LIST_REMOVE', index)
            }
            else {
                this.$store.commit('BULK_STUDENT_LIST_ADD', item.id)
            }
        },
        areAllSelected() {
            if (this.items.length <= 0) return false
            for (var item of this.items) {
                if (!contains(this.bulkStudents, (student) => item.id == student)) {
                    return false
                }
            }
            return true
        },
        toggleAll(state) {
            if (state) {
                this.$store.commit('BULK_STUDENT_LIST_CLEAR')
                for (var item of this.items) {
                    this.$store.commit('BULK_STUDENT_LIST_ADD', item.id)
                }
            }
            else {
                this.$store.commit('BULK_STUDENT_LIST_CLEAR')
            }
        },
        phoneFormat,
        apaFormat,
        nameFormat,
        isMobile
    }
}
</script>

<style lang="stylus">
.student-table
    &.el-table .cell, &.el-table th div
        padding-right: 5px
        padding-left: 5px

        .status-block
            margin-left: 0px !important
            margin-right: 0px

.bulk-actions
    display: inline-block
</style>
